import React, { createContext, useContext, useState, useEffect } from 'react';
import { calculateCustomPrice } from '../utils/pricingCalculator';
import { syncSavedCarts } from '../utils/recoveryCartSync';

const CartContext = createContext();
let cartLineSequence = 0;
const newCartLineId = () => globalThis.crypto?.randomUUID?.() || `${Date.now()}_${++cartLineSequence}`;

export const CartProvider = ({ children }) => {
  // Lazy-init from sessionStorage: an effect-based load raced the persist
  // effect (which fires on first render with []), so a page RELOAD on
  // checkout wiped the saved cart and bounced the customer to the
  // storefront before the load ever applied (v4.29.1).
  const [cartItems, setCartItems] = useState(() => {
    try {
      return JSON.parse(sessionStorage.getItem('storefrontCart')) || [];
    } catch (_e) {
      return [];
    }
  });
  const [isOpen, setIsOpen] = useState(false);

  // Save cart to sessionStorage whenever it changes
  useEffect(() => {
    sessionStorage.setItem('storefrontCart', JSON.stringify(cartItems));
    const timer = setTimeout(() => syncSavedCarts(cartItems), 1000);
    return () => clearTimeout(timer);
  }, [cartItems]);

  const addToCart = (mealPlan, options) => {
    const cartItem = {
      id: `${mealPlan.id}_${options.quantity}_${options.purchaseType}_${newCartLineId()}`,
      meal_plan_id: mealPlan.id,
      meal_plan_name: mealPlan.alias || mealPlan.name,  // Use alias if available
      currency: mealPlan.currency,
      price: options.price || calculatePrice(mealPlan, options.quantity),
      quantity: options.quantity,
      purchase_type: options.purchaseType,
      ...(options.packSize ? { pack_size: options.packSize, pack_selections: options.packSelections,
        pack_pricing_mode: options.packPricingMode || 'fixed_pack',
        pack_addons: options.packAddons || [], selected_addon_names: options.selectedAddonNames || [],
        price_comparison: options.priceComparison, timezone: options.timezone,
        menu_revision: options.menuRevision, selected_meal_names: options.selectedMealNames, cutoff_at: options.cutoffAt } : {}),
      ...(options.recurring ? { recurring: true, recurring_interval_weeks: options.recurringIntervalWeeks || null } : {}),
      delivery_days: options.deliveryDays,
      delivery_charge: options.deliveryCharge || 0,
      store_name: mealPlan.store_name || '',
      store_slug: options.storeSlug || '',  // Add store slug for backend mapping
      store_id: options.storeId || null,  // Direct store ID
      image: mealPlan.images?.[0] || null,
      start_date: options.startDate || '',
      delivery_instructions: options.deliveryInstructions || '',
      is_custom_order: options.isCustomOrder || false,
      custom_meal_items: options.customMealItems || null,
      is_addon: options.isAddon || false,  // Track if this is an add-on item
      // v4.12 — guided add-on wizard: cart_group_id links a plan to the
      // add-ons configured against it (checkout attaches them to THAT master
      // instead of a date-window guess); deliveries_per_day is the ×N/day
      // multiplier so an add-on can be delivered multiple times per day.
      cart_group_id: options.cartGroupId || null,
      deliveries_per_day: options.deliveriesPerDay || null
    };

    // v4.12 — FUNCTIONAL updater so multiple synchronous adds accumulate.
    // The guided add-on wizard adds the plan + each add-on in one tick; the
    // old `[...cartItems, item]` form read a stale array on the 2nd+ call and
    // dropped the earlier items (only the last survived).
    setCartItems(prev => [...prev, cartItem]);
    // Removed: setIsOpen(true); - Don't auto-open cart drawer
  };

  // ─── Meal pass items ────────────────────────────────────────────────
  // Meal passes are prepaid credits — no delivery, no schedule, just quantity × price.
  // They share the cart with meal plans so a customer buys everything in ONE checkout
  // (unified flow, single Stripe payment). Line items are flagged `is_meal_pass: true`
  // and carry `meal_pass_program_id` so the backend can dispatch credit grants on
  // checkout success. Total = price × quantity; no delivery charge.
  const addMealPassToCart = (program, options = {}) => {
    const quantity = Math.max(1, Number(options.quantity) || 1);
    const unitPrice = Number(program.price) || 0;
    const cartItem = {
      id: `pass_${program.id}_${quantity}_${Date.now()}`,
      is_meal_pass: true,
      meal_pass_program_id: program.id,
      meal_plan_id: program.id,       // duplicate for legacy code paths that read meal_plan_id
      meal_plan_name: program.public_name || program.name,
      price: unitPrice * quantity,
      unit_price: unitPrice,
      quantity,
      credits_per_pass: program.credit_count || 0,
      total_credits: (program.credit_count || 0) * quantity,
      purchase_type: 'one-time',
      delivery_days: [],
      delivery_charge: 0,
      store_name: options.storeName || '',
      store_slug: options.storeSlug || '',
      store_id: options.storeId || null,
      image: program.image_url || null,
      start_date: '',
      delivery_instructions: '',
      is_custom_order: false,
      custom_meal_items: null,
      is_addon: false,
      currency: program.currency || 'CAD',
      savings_label: program.savings_label || null,
    };
    setCartItems(prev => [...prev, cartItem]);
  };

  // ─── Catering items (v4.24) ─────────────────────────────────────────
  // A catering line is one configured event: package + headcount + event
  // date/time + dish/tray selections + premium upsells. Flagged
  // `is_catering: true`; the backend RE-PRICES from the package doc and
  // enforces min-persons / lead-time / event-hours fail-closed at checkout —
  // the price here is display-only. meal_plan_id/name are duplicated for
  // legacy display paths, same as meal passes.
  const addCateringToCart = (pkg, options = {}) => {
    const cartItem = {
      id: `catering_${pkg.id}_${Date.now()}`,
      is_catering: true,
      catering_package_id: pkg.id,
      meal_plan_id: pkg.id,           // duplicate for legacy code paths
      meal_plan_name: pkg.name,
      price: Number(options.price_total) || 0,
      per_person_price: Number(options.per_person) || 0,
      quantity: Math.max(1, Number(options.persons) || 1),
      persons: Math.max(1, Number(options.persons) || 1),
      event_date: options.event_date || '',
      event_time: options.event_time || '',
      fulfillment_method: options.fulfillment_method === 'delivery' ? 'delivery' : 'pickup',
      catering_selections: options.selections || [],
      catering_tray_selections: options.tray_selections || [],
      catering_premium_ids: options.premium_ids || [],
      // v5.17 — package add-on picks + removals (KEYS only; server re-prices).
      catering_addons: options.catering_addons || [],
      catering_removed_ingredients: options.catering_removed_ingredients || [],
      catering_notes: options.notes || '',
      purchase_type: 'one_time',
      delivery_days: [],
      delivery_charge: 0,
      store_name: options.storeName || '',
      store_slug: options.storeSlug || '',
      store_id: options.storeId || null,
      image: pkg.image_url || null,
      start_date: options.event_date || '',
      delivery_instructions: '',
      is_custom_order: false,
      custom_meal_items: null,
      is_addon: false,
    };
    setCartItems(prev => [...prev, cartItem]);
  };

  const removeFromCart = (itemId) => {
    setCartItems(cartItems.filter(item => item.id !== itemId));
  };

  const updateCartItem = (itemId, updates) => {
    setCartItems(cartItems.map(item => 
      item.id === itemId ? { ...item, ...updates } : item
    ));
  };

  const removePurchasedItems = (purchasedIds) => {
    if (!Array.isArray(purchasedIds)) return;
    setCartItems(current => current.filter(item => !purchasedIds.includes(item.id)));
  };

  const clearCart = () => {
    setCartItems([]);
    sessionStorage.removeItem('storefrontCart');
  };

  const restoreCart = items => setCartItems(items);

  const getCartTotal = () => {
    return cartItems.reduce((total, item) => total + (item.price || 0), 0);
  };

  const getCartCount = () => {
    return cartItems.length;
  };

  const calculatePrice = (mealPlan, quantity) => {
    const pricing = mealPlan.pricing || {};
    const pricingTiers = pricing.tiers || [];
    
    // Use the pricing calculator utility
    return calculateCustomPrice(quantity, pricingTiers);
  };

  return (
    <CartContext.Provider value={{
      cartItems,
      isOpen,
      setIsOpen,
      addToCart,
      addMealPassToCart,
      addCateringToCart,
      removeFromCart,
      updateCartItem,
      clearCart,
      restoreCart,
      removePurchasedItems,
      getCartTotal,
      getCartCount,
      calculatePrice
    }}>
      {children}
    </CartContext.Provider>
  );
};

export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart must be used within CartProvider');
  }
  return context;
};
